import theme from '@/plugins/theme'
import { h } from 'vue'
import { createVuetify } from 'vuetify'
import 'vuetify/styles'

// Self-hosted icon set — every icon is one of our own SVGs (no icon fonts).
// Aliases map Vuetify's internal `$names` and the app's semantic names to SVG
// components; `<v-icon icon="$name" />` renders the component inline and inherits
// colour via `currentColor`, exactly as the old fa/mdi font glyphs did.
import IconClose from '@svg/IconClose.svg?skipsvgo'
import IconCrossCircle from '@svg/Icon_Cross_Circle.svg?skipsvgo'
import IconCrossCircleFill from '@svg/Icon_Cross_Circle_Fill.svg?skipsvgo'
import IconCheck from '@svg/Icon_CheckboxTick.svg?skipsvgo'
import IconCheckCircleFill from '@svg/Icon_Checkbox_Circle_Fill.svg?skipsvgo'
import IconChevronDown from '@svg/Icon_ChevronDown.svg?skipsvgo'
import IconChevronDownSmall from '@svg/Icon_ChevronDownSmall.svg?skipsvgo'
import IconChevronUp from '@svg/Icon_ChevronUp.svg?skipsvgo'
import IconChevronLeft from '@svg/Icon_ChevronLeft.svg?skipsvgo'
import IconChevronRight from '@svg/Icon_ChevronRight.svg?skipsvgo'
import IconInfoCircle from '@svg/Icon_InfoCircle.svg?skipsvgo'
import IconWarning from '@svg/Icon_Warning.svg?skipsvgo'
import IconWarningFill from '@svg/Icon_Warning_Fill.svg?skipsvgo'
import IconAlertFill from '@svg/Icon_Alert_Fill.svg?skipsvgo'
import IconCalendar from '@svg/Icon_Calendar.svg?skipsvgo'
import IconClock from '@svg/Icon_Clock.svg?skipsvgo'
import IconMenu from '@svg/Icon_Menu.svg?skipsvgo'
import IconBan from '@svg/Icon_Ban.svg?skipsvgo'
import IconWrench from '@svg/Icon_Wrench.svg?skipsvgo'
import IconArrowUp from '@svg/Icon_ArrowUp.svg?skipsvgo'
import IconArrowDown from '@svg/Icon_ArrowDown.svg?skipsvgo'
import IconLink from '@svg/Icon_Link.svg?skipsvgo'
import IconExternalLink from '@svg/IconOutLink.svg?skipsvgo'
import IconFileUpload from '@svg/IconUploadFile.svg?skipsvgo'
import IconAdd from '@svg/Icon_Add.svg?skipsvgo'
import IconUser from '@svg/IconUser.svg?skipsvgo'
import IconUsersFour from '@svg/IconUsersFour.svg?skipsvgo'
// Form controls keep their design-system state fills (selected/indeterminate/etc).
import IconCheckboxOff from '@svg/IconCheckbox.svg?skipsvgo'
import IconCheckboxOn from '@svg/Icon_CheckboxSelected.svg?skipsvgo'
import IconCheckboxIndeterminate from '@svg/Icon_CheckboxIndeterminate.svg?skipsvgo'
import IconRadioOff from '@svg/Icon_Radio.svg?skipsvgo'
import IconRadioOn from '@svg/Icon_RadioSelected.svg?skipsvgo'

const aliases = {
  // --- Vuetify internal aliases (form controls, clear/close, carets, alerts) ---
  complete: IconCheck,
  cancel: IconCrossCircle,
  close: IconClose,
  delete: IconClose,
  clear: IconClose,
  success: IconCheckCircleFill,
  info: IconInfoCircle,
  warning: IconAlertFill,
  error: IconCrossCircleFill,
  prev: IconChevronLeft,
  next: IconChevronRight,
  checkboxOn: IconCheckboxOn,
  checkboxOff: IconCheckboxOff,
  checkboxIndeterminate: IconCheckboxIndeterminate,
  radioOn: IconRadioOn,
  radioOff: IconRadioOff,
  expand: IconChevronDown,
  collapse: IconChevronUp,
  dropdown: IconChevronDownSmall,
  subgroup: IconChevronDown,
  menu: IconMenu,
  file: IconFileUpload,
  sortAsc: IconArrowUp,
  sortDesc: IconArrowDown,
  // --- App semantic aliases (replace the old fa-*/mdi-* strings) ---
  check: IconCheck,
  checkCircle: IconCheckCircleFill,
  closeCircle: IconCrossCircle,
  chevronDown: IconChevronDown,
  arrowUp: IconArrowUp,
  alert: IconWarningFill,
  warningTriangle: IconWarning,
  infoCircle: IconInfoCircle,
  calendar: IconCalendar,
  clock: IconClock,
  bars: IconMenu,
  ban: IconBan,
  wrench: IconWrench,
  link: IconLink,
  externalLink: IconExternalLink,
  fileUpload: IconFileUpload,
  plusSquare: IconAdd,
  userTie: IconUser,
  usersFour: IconUsersFour,
}

// All app/Vuetify icons resolve to components via `aliases` above (Vuetify routes
// non-string icons straight to its internal component renderer). This fallback set
// only sees bare string icons — none should remain after the font removal, so it
// renders an empty tag rather than broken text.
const jigIcons = {
  component: (props: any) => h(props.tag),
}

// 'anchor' is not a Vuetify 3 theme color; link color is handled via CSS where needed.
const { anchor, ...colors } = theme

export default createVuetify({
  // Vuetify 3 renders inputs and lists larger than Vuetify 2 (taller fields, an
  // always-reserved empty messages row under every input, roomier list rows).
  // These global component defaults restore develop's compact look everywhere
  // without per-template edits. Components that set their own density/hide-details
  // (e.g. JigTextField) override these. `hideDetails: 'auto'` collapses the empty
  // messages row but still shows validation/hint messages when present.
  defaults: {
    // Bound prose tooltips; the connected overlay also clamps them to the viewport.
    VTooltip: { maxWidth: 320 },
    VTextField: { density: 'compact', hideDetails: 'auto' },
    // menuIcon: a thin chevron app-wide (Vuetify's default is a heavy caret).
    VSelect: { density: 'compact', hideDetails: 'auto', menuIcon: '$dropdown' },
    VAutocomplete: { density: 'compact', hideDetails: 'auto', menuIcon: '$dropdown' },
    VCombobox: { density: 'compact', hideDetails: 'auto', menuIcon: '$dropdown' },
    VTextarea: { hideDetails: 'auto' },
    VSwitch: { density: 'compact', hideDetails: 'auto' },
    VCheckbox: { density: 'compact', hideDetails: 'auto' },
    VList: { density: 'compact' },
  },
  icons: {
    defaultSet: 'jig',
    aliases,
    sets: { jig: jigIcons },
  },
  theme: {
    defaultTheme: 'light',
    themes: { light: { colors } },
  },
})
